/**
 * §10.4「Session 過期 → 跳登入對話框，**不離開目前畫面**。登入成功後直接重送」。
 *
 * 為什麼這件事值得一個對話框而不是直接跳回登入頁：
 * 現場可能正在輸入第 180 片的重量（§10.4：逐片輸入 200 筆是最怕重來的操作）。
 * 把人踢回登入頁等於那 180 筆重打。
 */

import { useState } from 'react'
import { ApiError, api } from '../api/client.js'
import { Button, Field, TextInput } from '../components/ui.js'
import { useAuth } from './AuthContext.js'

export function ReauthDialog() {
  const { reauthOpen, resolveReauth, me, methods } = useAuth()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!reauthOpen) return null

  const email = me?.user.email ?? ''

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.post('/api/auth/login', { email, password }, { noReauth: true })
      setPassword('')
      resolveReauth(true)   // client.ts 會用同一個 Idempotency-Key 重送原本的請求
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/50 p-4">
      <div className="w-full max-w-sm rounded-lg border border-ink-200 bg-white p-5 shadow-xl">
        <h2 className="font-semibold text-ink-900">登入已逾時</h2>
        <p className="mt-1 text-sm text-ink-600">
          您填的內容都還在。重新登入之後會自動繼續，不必重打。
        </p>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <Field label="帳號">
            <TextInput value={email} readOnly className="bg-ink-50 text-ink-500" />
          </Field>
          {methods?.password !== false && (
            <Field label="密碼" required>
              <TextInput
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}
          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" className="flex-1" disabled={busy}>
              {busy ? '請稍候…' : '重新登入並繼續'}
            </Button>
            <Button
              type="button"
              onClick={() => { setPassword(''); resolveReauth(false) }}
              disabled={busy}
            >取消</Button>
          </div>
        </form>

        <p className="mt-3 text-[11px] text-ink-400">
          取消的話這次操作不會送出，但畫面上的內容仍然保留。
        </p>
      </div>
    </div>
  )
}
